<template>
    <div class="property-details-page bg-neutrals-surface min-h-screen">
        <main v-if="property" class="container mx-auto p-4 md:p-8">

            <div class="bg-neutrals-background rounded-xl shadow-xl overflow-hidden mb-6">
                <v-carousel 
                    v-if="property.images && property.images.length > 0"
                    :show-arrows="property.images.length > 1" 
                    :cycle="false" 
                    hide-delimiter-background 
                    height="400"
                    :class="`rounded-xl cardPropertyCarousel`"  
                    :delimiter-icon="`mdi-circle`">
                    
                    <v-carousel-item 
                        v-for="(img, i) in property.images" 
                        :key="i" 
                        :src="img" 
                        alt="Imagem do imóvel"
                        cover>
                    </v-carousel-item>
                </v-carousel>
                
                <div v-else class="flex justify-center items-center h-[400px] text-neutrals-muted bg-gray-200">
                    <p>Nenhuma imagem disponível.</p>
                </div>
            </div>

            <header class="mb-8 text-center md:text-left">
                <h1 class="text-3xl font-extrabold text-neutrals-text mb-2">{{ property.title }}</h1>
                <p class="text-xl text-neutrals-muted">{{ property.neighborhood }}, {{ property.city }} - {{ property.state }}
                </p>
            </header>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div class="lg:col-span-2 space-y-6">

                    <div class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-3">Descrição</h2>
                        <p class="text-neutrals-text leading-relaxed">{{ property.description }}</p>
                    </div>

                    <div v-if="highlightsArray.length > 0" class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-3">Pontos Fortes</h2>
                        <ul class="list-disc list-inside text-neutrals-text space-y-1">
                            <li v-for="(item, index) in highlightsArray" :key="index">{{ item }}</li>
                        </ul>
                    </div>

                    <div v-if="featuresArray.length > 0" class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-3">Características</h2>
                        <div class="flex flex-wrap gap-3">
                            <span v-for="(feature, index) in featuresArray" :key="index"
                                class="bg-secondary/10 text-secondary text-sm font-medium px-4 py-2 rounded-full shadow-sm">
                                {{ formatFeature(feature) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="property.reference_point" class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-3">Ponto de Referência</h2>
                        <p class="text-neutrals-text">{{ property.reference_point }}</p>
                    </div>
                </div>

                <aside class="space-y-6">
                    <div class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <div class="flex items-baseline mb-2">
                            <span class="text-3xl font-extrabold text-secondary">{{ formatCurrency(property.price)
                                }}</span>
                            <span v-if="property.operation === 'rent'" class="text-sm text-neutrals-muted ml-1">/mês</span>
                        </div>
                        <p class="text-sm text-neutrals-muted mb-4">Cód: {{ formatCode(property) }}</p>
                        <a :href="getWhatsappLink(property)" target="_blank"
                            class="w-full flex items-center justify-center bg-secondary text-white font-bold py-3 rounded-lg shadow-md hover:bg-secondary/90 transition transform hover:scale-105">
                            <i class="fab fa-whatsapp text-lg mr-2"></i> Falar com o Corretor
                        </a>
                    </div>

                    <div class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-4">Informações Gerais</h2>
                        <ul class="space-y-3 text-neutrals-text">
                            <li><span class="font-semibold">Tipo:</span> {{ formatType(property.type) }}</li>
                            <li><span class="font-semibold">Operação:</span> {{ formatOperation(property.operation) }}
                            </li>
                            <li v-if="property.for_rent"><span class="font-semibold">Disponível para:</span> Aluguel
                            </li>
                            <li v-if="property.for_sale"><span class="font-semibold">Disponível para:</span> Venda</li>
                            <li v-if="property.is_launch"><span class="font-semibold">Lançamento:</span> Sim</li>
                            <li v-if="property.is_reserved"><span class="font-semibold">Reservado:</span> Sim</li>
                        </ul>
                    </div>

                    <div class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-4">Áreas do Imóvel</h2>
                        <ul class="space-y-2 text-neutrals-text">
                            <li v-if="property.total_area"><span class="font-semibold">Área Total:</span> {{
                                property.total_area }} m²</li>
                            <li v-if="property.usable_area"><span class="font-semibold">Área Útil:</span> {{
                                property.usable_area }} m²</li>
                            <li v-if="property.built_area"><span class="font-semibold">Área Construída:</span> {{
                                property.built_area }} m²</li>
                            <li v-if="property.lot_area"><span class="font-semibold">Área do Lote:</span> {{
                                property.lot_area }} m²</li>
                            <li v-if="property.lot_front"><span class="font-semibold">Frente:</span> {{
                                property.lot_front }} m</li>
                            <li v-if="property.lot_back"><span class="font-semibold">Fundos:</span> {{ property.lot_back
                                }} m</li>
                        </ul>
                    </div>

                    <div v-if="property.iptu_value" class="bg-neutrals-background p-6 rounded-xl shadow-lg">
                        <h2 class="text-2xl font-bold text-secondary mb-4">IPTU</h2>
                        <p class="text-neutrals-text">Valor: {{ formatCurrency(property.iptu_value) }}</p>
                        <p v-if="property.iptu_periodicity" class="text-neutrals-muted text-sm">
                            Periodicidade: {{ formatIptu(property.iptu_periodicity) }}
                        </p>
                    </div>
                </aside>
            </div>
        </main>

        <div v-else class="flex justify-center items-center h-screen">
            <p class="text-neutrals-muted text-lg animate-pulse">Carregando...</p>
        </div>

        <AppFooter />
        <Copyright />
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import AppFooter from "@/components/AppFooter.vue";
import Copyright from "@/components/Copyright.vue";
import api from "@/services/api";
// REMOÇÃO DOS IMPORTS DO SWIPER
import AOS from 'aos';
import 'aos/dist/aos.css';

const props = defineProps({
    slug: { type: String, required: true },
});

const enterprise = ref({});
const property = ref(null);
const route = useRoute();
const app_url_api_storage = process.env.VUE_APP_URL_API_STORAGE;

const highlightsArray = computed(() => {
    return property.value?.highlights
        ? property.value.highlights
            .split("\n")
            .map((i) => i.trim())
            .filter((i) => i.length > 0)
        : [];
});

const featuresArray = computed(() => {
    if (property.value?.features) {
        try {
            const parsed = JSON.parse(property.value.features);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }
    return [];
});

const fetchPropertyDetails = async () => {
    try {
        const response = await api.get(`properties/slug/${props.slug}`);
        property.value = response.data.data;

        if (Array.isArray(property.value.images)) {
            property.value.images = property.value.images.map(img => `${app_url_api_storage}/images/${img}`);
        } else {
            property.value.images = [];
        }
    } catch (error) {
        console.error("Erro ao buscar detalhes do imóvel:", error);
        toastStore.showToast({
            message: error?.response?.data?.error?.error || 'Erro ao localizar imóvel',
            color: 'error',
        });
    }
};

const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(value);

const formatOperation = (op) =>
    ({ rent: "Aluguel", sale: "Venda", both: "Aluguel e Venda" }[op] || op);

const formatType = (type) =>
({
    apartment: "Apartamento",
    house: "Casa",
    land: "Terreno",
    commercial: "Comercial",
    rural: "Rural",
}[type] || type);

const formatStatus = (status) =>
({ in_analysis: "Em Análise", approved: "Aprovado", rejected: "Reprovado" }[
    status
] || status);

const formatIptu = (period) =>
({
    monthly: "Mensal",
    annually: "Anual",
    single_payment: "Pagamento Único",
}[period] || period);

const formatFeature = (feature) => {
    const featureMap = {
        accepts_pets: "Aceita Pet",
        closets: "Armários",
        calm_location: "Local Tranquilo",
        cold_floor: "Piso Frio",
        backyard: "Quintal",
        fan: "Ventilador",
        service_area: "Área de Serviço",
        kitchen: "Cozinha",
        school: "Escola Próxima",
        pharmacy: "Farmácia Próxima",
    };
    return featureMap[feature] || feature.replace(/_/g, " ");
};

const getWhatsappLink = (property) => {
    const phoneNumberCleaned = enterprise.value?.phone ? enterprise.value.phone.replace(/[()\s-.]/g, '') : null;

    const message = `Olá, tenho interesse neste imóvel de código ${formatCode(property)}, localizado em ${property.neighborhood}, ${property.city} - ${property.state}.`;

    if (phoneNumberCleaned) {
        return `https://api.whatsapp.com/send?phone=${phoneNumberCleaned}&text=${encodeURIComponent(message)}`;
    }

    return '#';
};

function formatCode(property) {
    let code = null;
    if (property.operation === 'rent') {
        code = 'L-';
    } else if (property.operation === 'sale') {
        code = 'V-';
    }
    else if (property.operation === 'launch') {
        code = 'LA-';
    }
    else if (property.operation === 'both') {
        code = 'VL-';
    } else {
        code = '';
    }

    code = code + property.id + '-' + property.year;

    return code;
}

const loadEnterprise = async () => {
    try {
        const response = await api.get(`enterprise`);
        enterprise.value = response.data.data[0];
    } catch (error) {
        console.error("Erro ao buscar empresa:", error);
        toastStore.showToast({
            message: error?.response?.data?.error?.error || 'Erro ao buscar empresa',
            color: 'error',
        });
    }
};

onMounted(async () => {
    await loadEnterprise();
    await fetchPropertyDetails();
    AOS.init();
});
</script>

<style scoped>
.cardPropertyCarousel :deep(.v-carousel__controls) {
  /* CHAVE: Força os bullets a ficarem DENTRO da imagem, 16px acima do fundo */
  bottom: 16px !important;
  top: auto !important;
  /* Anula qualquer posicionamento superior */
  padding: 2px 8px !important;
  background: transparent !important;
  height: 18px;
  z-index: 10 !important;
}

/* 2. Bolinhas (Bullets) de Paginação */
.cardPropertyCarousel :deep(.v-btn--icon.v-btn--size-small) {
  background-color: #d1d5db !important;
  height: 8px !important;
  width: 8px !important;
  margin: 0 4px;
  border-radius: 50%;
  color: transparent !important;
  padding: 0 !important;
  min-width: 8px !important;
  opacity: 1 !important;
}

.cardPropertyCarousel :deep(.v-btn--active.v-btn--size-small) {
  background-color: #1583d4 !important;
  border: none;
}

/* 3. Setas de Navegação (Navigation Arrows) */
.cardPropertyCarousel :deep(.v-btn--icon.v-btn--size-default) {
  background-color: rgba(0, 0, 0, 0.3) !important;
  color: white !important;
  opacity: 0.7;
  height: 30px !important;
  width: 30px !important;
  border-radius: 50% !important;
  transition: opacity 0.3s, background-color 0.3s;
}

/* Efeito Hover na Seta */
.cardPropertyCarousel :deep(.v-btn--icon.v-btn--size-default:hover) {
  opacity: 1 !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

/* Seta Desativada */
.cardPropertyCarousel :deep(.v-btn--disabled) {
  opacity: 0.3 !important;
  background-color: rgba(0, 0, 0, 0.1) !important;
}
</style>